import { supabase } from './supabaseClient.js';
import { logError } from '../lib/utils.js';

// Las ESCRITURAS de ordenes_pago salen por el backend, no por Supabase.
//
// POR QUE: las policies de la tabla estan acotadas por `empresa_usuarios` —del
// portal de clientes empresa—, que tiene cero filas, asi que desde el navegador
// son inertes. Y RLS no da error: filtra. El borrado afectaba a 0 filas y esta
// pantalla decia "eliminada correctamente".
//
// No se arregla con una policy para el staff: el token por defecto de Clerk solo
// trae `sub`, sin email ni rol, asi que RLS no tiene con que reconocerlo. Es el
// mismo motivo por el que los cargos salen por payment (yoolpay/chargesApi.js).
//
// Las LECTURAS se quedan como estan: `read_all` las permite a cualquier
// autenticado.
const getApiBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (backendUrl) return backendUrl.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return '';
  }
  return 'http://localhost:3010';
};

async function clerkToken() {
  try {
    return (await window?.Clerk?.session?.getToken?.()) || null;
  } catch {
    return null;
  }
}

/** Llama al backend con el JWT de staff. Lanza con el mensaje del servidor. */
async function apiOrdenesPago(path, { method, body } = {}) {
  const token = await clerkToken();
  const res = await fetch(`${getApiBaseUrl()}/api/ordenes-pago${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `Error ${res.status}`);
  }
  return data;
}

/**
 * La cadena de trazabilidad de una orden, en una sola consulta.
 *
 * `pagos` y `certificados` tienen RLS que exige `auth.role() = 'authenticated'`,
 * así que estos embeds vuelven vacíos con el cliente anónimo aunque las filas
 * existan. Quien llame debe pasar el cliente con token de Clerk
 * (`useSupabaseClient()`); el `supabase` anónimo queda solo como respaldo.
 */
/**
 * LAS N CONTRATACIONES DE UNA ORDEN MULTIPRODUCTO
 *
 * `ordenes_pago.contratacion_id` apunta a UNA sola contratación, la primaria. Un
 * carrito de N productos genera N contrataciones que comparten una orden, un pago
 * y una suscripción, así que ese campo se queda con la primera y las hermanas
 * quedan invisibles: la pantalla mostraba CT-2607-1486 junto al certificado
 * CE-2607-1315, que es del OTRO contrato, y CT-2607-1487 no aparecía nunca.
 *
 * El carrito es lo que las une —`contrataciones.carrito_id`—, y se llega en la
 * misma consulta: orden → contratación primaria → su carrito → todas las
 * contrataciones del carrito. `ordenes_pago.carrito_id` NO sirve para esto: en
 * las órdenes multiproducto verificadas viene NULL.
 *
 * Una compra de un solo producto devuelve una lista de un elemento: misma forma,
 * sin ramas especiales.
 */
const CONTRATACIONES_DEL_CARRITO = `carritos(id, contrataciones(id, codigo, estado, precio_base_id))`;

const SELECT_TRAZABILIDAD = `
  *,
  contrataciones(id, codigo, nombre, mail, rut, estado, carrito_id, ${CONTRATACIONES_DEL_CARRITO}),
  pagos(id, codigo, status, transaction_date, certificados(id, codigo, estado)),
  subscriptions(id, codigo, status, subscription_id, contrataciones_data, certificados(id, codigo, estado))
`;

const SELECT_TRAZABILIDAD_DETALLE = `
  *,
  contrataciones(id, codigo, nombre, mail, rut, estado, datos_titular, cotizacion_id, carrito_id,
        ${CONTRATACIONES_DEL_CARRITO}),
  pagos(id, codigo, amount, status, transaction_date, session_id, payment_provider, card_type, card_last_four,
        certificados(id, codigo, estado, fecha_emision, fecha_inicio_cobertura, fecha_termino_cobertura, archivo_id)),
  subscriptions(id, codigo, status, subscription_id, plan_name, payment_provider, amount, currency,
        billing_frequency, start_date, next_billing_date, contrataciones_data,
        certificados(id, codigo, estado, fecha_emision, fecha_inicio_cobertura, fecha_termino_cobertura, archivo_id))
`;

/** Los filtros de listado, aplicados igual con o sin trazabilidad. */
const aplicarFiltros = (query, filters = {}) => {
  if (filters.estado) query = query.eq('estado', filters.estado);
  if (filters.contratacion_id) query = query.eq('contratacion_id', filters.contratacion_id);
  if (filters.fecha_desde) query = query.gte('created_at', filters.fecha_desde);
  if (filters.fecha_hasta) query = query.lte('created_at', filters.fecha_hasta);
  if (filters.monto_min) query = query.gte('monto', filters.monto_min);
  if (filters.monto_max) query = query.lte('monto', filters.monto_max);
  return query;
};

/**
 * Servicio para gestión de órdenes de pago
 */
export class OrdenespagoService {
  /**
   * Obtener todas las órdenes de pago con filtros opcionales
   */
  static async getOrdenesPago(filters = {}, client = supabase) {
    try {
      let query = client
        .from('ordenes_pago')
        .select(`
          *,
          contrataciones(id, codigo, nombre, mail, rut, estado)
        `);

      query = aplicarFiltros(query, filters);

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      logError('Error fetching ordenes de pago:', error, { ignoreAbort: true });
      throw error;
    }
  }

  /**
   * Igual que `getOrdenesPago`, pero arrastrando la cadena completa: de qué
   * contratación nació cada orden, qué pago la cerró, qué suscripción quedó viva
   * y qué certificados se emitieron. Es la consulta que alimenta la columna de
   * trazabilidad de la lista.
   */
  static async getOrdenesPagoConTrazabilidad(filters = {}, client = supabase) {
    try {
      let query = client.from('ordenes_pago').select(SELECT_TRAZABILIDAD);

      query = aplicarFiltros(query, filters);

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      logError('Error fetching ordenes de pago con trazabilidad:', error, { ignoreAbort: true });
      throw error;
    }
  }

  /**
   * Obtener una orden de pago por ID, con toda su cadena de trazabilidad
   */
  static async getOrdenPagoById(id, client = supabase) {
    try {
      const { data, error } = await client
        .from('ordenes_pago')
        .select(SELECT_TRAZABILIDAD_DETALLE)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching orden de pago:', error);
      throw error;
    }
  }

  /**
   * Crear una nueva orden de pago
   */
  static async createOrdenPago(ordenData) {
    try {
      const { data, error } = await supabase
        .from('ordenes_pago')
        .insert([
          {
            contratacion_id: ordenData.contratacion_id,
            monto: ordenData.monto,
            estado: ordenData.estado || 'pendiente',
            fecha_expiracion: ordenData.fecha_expiracion
          }
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating orden de pago:', error);
      throw error;
    }
  }

  /**
   * Actualizar una orden de pago
   */
  static async updateOrdenPago(id, updates) {
    try {
      const { data, error } = await supabase
        .from('ordenes_pago')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating orden de pago:', error);
      throw error;
    }
  }

  /**
   * Cambiar estado de una orden de pago
   */
  static async cambiarEstado(id, nuevoEstado) {
    try {
      // El backend revalida el estado: esta comprobacion es solo para no hacer
      // el viaje si ya se sabe que no vale.
      const estadosValidos = ['pendiente', 'procesando', 'pagado', 'expirado', 'cancelado'];
      if (!estadosValidos.includes(nuevoEstado)) {
        throw new Error(`Estado inválido: ${nuevoEstado}`);
      }

      const { orden } = await apiOrdenesPago(`/${id}`, {
        method: 'PATCH',
        body: { estado: nuevoEstado },
      });
      return orden;
    } catch (error) {
      console.error('Error cambiando estado de orden de pago:', error);
      throw error;
    }
  }

  /**
   * Eliminar una orden de pago
   */
  static async deleteOrdenPago(id) {
    try {
      // El backend devuelve 404 si no borro nada, para que un id inexistente
      // deje de parecer un exito.
      await apiOrdenesPago(`/${id}`, { method: 'DELETE' });
      return true;
    } catch (error) {
      console.error('Error deleting orden de pago:', error);
      throw error;
    }
  }

  /**
   * Obtener órdenes de pago expiradas
   */
  static async getOrdenesExpiradas() {
    try {
      const { data, error } = await supabase
        .from('ordenes_pago')
        .select(`
          *,
          contrataciones(id, nombre, mail)
        `)
        .lt('fecha_expiracion', new Date().toISOString())
        .eq('estado', 'pendiente')
        .order('fecha_expiracion', { ascending: true });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching órdenes expiradas:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de órdenes de pago
   */
  static async getEstadisticas(client = supabase) {
    try {
      const { data, error } = await client
        .from('ordenes_pago')
        .select('estado, monto');

      if (error) throw error;

      const stats = {
        total: data.length,
        pendientes: data.filter(o => o.estado === 'pendiente').length,
        procesando: data.filter(o => o.estado === 'procesando').length,
        pagadas: data.filter(o => o.estado === 'pagado').length,
        expiradas: data.filter(o => o.estado === 'expirado').length,
        canceladas: data.filter(o => o.estado === 'cancelado').length,
        montoTotal: data.reduce((sum, o) => sum + (parseFloat(o.monto) || 0), 0),
        montoPendiente: data
          .filter(o => o.estado === 'pendiente')
          .reduce((sum, o) => sum + (parseFloat(o.monto) || 0), 0),
        montoPagado: data
          .filter(o => o.estado === 'pagado')
          .reduce((sum, o) => sum + (parseFloat(o.monto) || 0), 0)
      };

      return stats;
    } catch (error) {
      logError('Error fetching estadísticas:', error, { ignoreAbort: true });
      throw error;
    }
  }
}

export default OrdenespagoService;
